import { Component, inject, input, OnInit, output, signal, viewChild, ChangeDetectionStrategy } from "@angular/core";
import { Venue, VenueZip } from "@interfaces";
import { TabStripComponent, TabStripModule } from "@progress/kendo-angular-layout";
import { VenueEditPart } from "../edit/venue-edit.part";
import { VenueDialogDataService } from "../venue-dialog-data.service";

interface NearbyVenue extends Venue {
	distance: {
		zip: VenueZip,
		miles: number,
		kilometers: number,
	};
}

@Component({
	selector: 'choose-venue-part',
	standalone: true,
	imports: [TabStripModule,
		VenueEditPart,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './choose-venue.part.html'
})
export class VenueChooseVenuePart implements OnInit {

	private venueDialogDataService = inject(VenueDialogDataService);

	public zip = input.required<VenueZip>();
	public venueInitialChoice = input<Venue | undefined>(undefined);
	public selection = output<Venue | undefined>();
	public tabs = viewChild<TabStripComponent>('tabs');


	public options = signal<{ venueId: number, text: string, data: NearbyVenue }[]>([]);
	public selectedOption = signal<{ venueId: number, text: string, data: NearbyVenue } | undefined>(undefined);
	private nearByVenues = signal<NearbyVenue[]>([]);


	ngOnInit(): void {
		this.setData();
	}


	private setData(resetIntialChoice: boolean = false) {

		let venueInitialChoice = this.venueInitialChoice();

		if (resetIntialChoice) venueInitialChoice = undefined;
		//
		// If there is an initial venue, but there isn't already an option with that venue id,
		// then the venue was added and we should raise the selection event below.
		//
		const noIntialVenueFound = !this.options().find(o => o.venueId == venueInitialChoice?.venueId);
		this.valueChange(undefined);
		const nearbyVenues = this.getNearbyVenues(this.zip());
		this.nearByVenues.set(nearbyVenues);
		this.options.set(nearbyVenues.map(v => ({ venueId: v.venueId, text: v.address, data: v })));

		this.selectedOption.set(undefined);

		if (venueInitialChoice) {
			const selectedOption = this.options().find(o => o.venueId == venueInitialChoice!.venueId);
			this.selectedOption.set(selectedOption);
			if (noIntialVenueFound) this.valueChange(venueInitialChoice!.venueId);
		}
	}


	selectChange(e: any) {

		const select: HTMLSelectElement = e.target;

		let selectedVenueId: number | undefined;

		//
		// Hack around the "multiple" attribute by finding just the first selected.
		// Need a old for loop because old school dom elements don't support iterators.
		//
		for (let i = 0; i < select.options.length; i++) {
			const o = select.options[i];
			if (o?.selected) {
				selectedVenueId = +o.value;
			}
		}

		//
		// Unselect all but the first selected one.
		//
		if (selectedVenueId) {
			for (let i = 0; i < select.options.length; i++) {
				const o = select.options[i]!;
				o.selected = +o.value == selectedVenueId;
			}
		}

		//
		// Notify any change
		//
		if (this.selectedOption()?.venueId !== selectedVenueId) {
			const selectedOption = this.options().find(option => option.venueId == selectedVenueId);
			this.selectedOption.set(selectedOption);
			this.valueChange(selectedVenueId);
		}

	}


	valueChange(venueId: number | undefined) {
		if (venueId && isNaN(venueId) || !venueId) {
			this.selection.emit(undefined);
		}
		else {
			const selectedVenue = this.nearByVenues().find(v => v.venueId == venueId);
			this.selection.emit(selectedVenue);
		}
	}


	getMapUrl(venue: NearbyVenue) {
		return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.officialName)}&query_place_id=${venue.googlePlaceKey}`;
	}

	async onVenueChange(venue: Venue) {
		this.selection.emit(venue);
		// Select the first tab venue.
		this.tabs()?.selectTab(0);
	}

	onVenueDelete(venueId: number) {
		// Set the selected option and selected venue to undefined.
		this.selectedOption.set(undefined);
		this.setData(true);
	}


	/**
* Return a list of venues that are nearby a provided zip. The distances are
* from roughly the center of the zip code area. 
* @param zip The lat/lon of the zip will be used to calculate distances.
* @param withinMiles Include all venues within this many statue miles. Multiply by 0.621371 to convert kilometers to miles. 
*/
	public getNearbyVenues(zip: VenueZip, withinMiles = 50): NearbyVenue[] {
		//
		// Latitudes and longitudes are stored in the database as integers and
		// must be divided by 100 before used for calculations or used on a map.
		//
		const lat1 = zip.latitude / 100;
		const lon1 = zip.longitude / 100;

		return this.venueDialogDataService.venues()
			.map(venue => {
				const lat2 = venue.latitude / 100;
				const lon2 = venue.longitude / 100;
				let miles = this.calcDistance(lat1, lon1, lat2, lon2);
				let kilometers = miles * 0.621371;

				miles = Math.round(miles * 100) / 100;
				kilometers = Math.round(kilometers * 100) / 100;

				return { ...(venue as Venue), distance: { zip, miles, kilometers } };
			})
			.filter(nearbyVenue => nearbyVenue.zipId == zip.zipId || nearbyVenue.distance.miles <= withinMiles)
			.sort((v1, v2) => v1.distance.miles - v2.distance.miles);

	}


	/**
	 * Apply some spherical geometry to calculate the distance in miles. Note that
	 * the earth is not a perfect sphere so the actual calculated distances are
	 * only approximations.
	 */
	private calcDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
		const RAD = Math.PI / 180;

		const earthRadius = 3956; // statue miles (use 6367 for kilometers)
		const dLat = (lat2 - lat1) * RAD;
		const dLon = (lon2 - lon1) * RAD;
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(lat1 * RAD) * Math.cos(lat2 * RAD) *
			Math.sin(dLon / 2) * Math.sin(dLon / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return earthRadius * c;
	}

}